/**
 * Sidebar — 3 modes + optional resize
 * date_of_change: 2025-09-04
 * 
 * A sophisticated sidebar component supporting:
 * - Three modes: expanded (full content), icons (icon rail only), collapsed (hidden)
 * - Resizable width when expanded
 * - Persistent state via useDock hook
 * - Left/right positioning with proper layout
 */
import React, { useState } from 'react';
import { useDock } from './useDock';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

type Mode = 'expanded' | 'icons' | 'collapsed';

interface SidebarProps {
  id: 'left' | 'right';
  min?: number;
  max?: number;
  defaultWidth?: number;
  children: React.ReactNode;
  icons: React.ReactNode;
  resizable?: boolean;
  center?: React.ReactNode;
  className?: string;
  mode?: Mode;
  onModeChange?: (mode: Mode) => void;
  isCompact?: boolean;
  onRequestClose?: () => void;
  drawerTitle?: string;
}

export default function Sidebar({ 
  id, 
  min = 220, 
  max = 420, 
  defaultWidth = 280, 
  resizable = true, 
  children, 
  icons,
  center,
  className = '',
  mode: controlledMode,
  onModeChange,
  isCompact = false,
  onRequestClose,
  drawerTitle = 'Side panel'
}: SidebarProps) {
  const internalDock = useDock(`sidebar:${id}`, 'expanded');
  const mode = controlledMode ?? internalDock.mode;
  const setMode = onModeChange ?? internalDock.setMode;
  
  const [px, setPx] = useState(defaultWidth);

  // Control buttons component that can be reused
  const ControlButtons = ({ className = '' }: { className?: string }) => (
    <div className={`battle-sidebar__mode-controls flex flex-row gap-2 backdrop-blur-sm p-2 rounded-lg shadow-md border ${className}`}>
      <button 
        className={`battle-sidebar__mode-button w-8 h-8 p-0 flex items-center justify-center rounded-full transition-all ${mode === 'expanded' ? 'is-active shadow-inner' : ''}`}
        onClick={() => setMode('expanded')} 
        title="Expanded View"
        aria-label="Show expanded view"
        aria-pressed={mode === 'expanded'}
      >
        <ChevronRight size={16} className={mode === 'icons' ? 'rotate-90' : ''} />
      </button>
      <button 
        className={`battle-sidebar__mode-button w-8 h-8 p-0 flex items-center justify-center rounded-full transition-all ${mode === 'icons' ? 'is-active shadow-inner' : ''}`}
        onClick={() => setMode('icons')} 
        title="Icons Only"
        aria-label="Show icons only"
        aria-pressed={mode === 'icons'}
      >
        <div className="w-2 h-2 rounded-full bg-current" />
      </button>
      <button 
        className="battle-sidebar__mode-button battle-sidebar__mode-button--collapse w-8 h-8 p-0 flex items-center justify-center rounded-full transition-all"
        onClick={() => setMode('collapsed')} 
        title="Collapse"
        aria-label="Collapse sidebar"
      >
        <ChevronLeft size={16} className={mode === 'icons' ? '-rotate-90' : ''} />
      </button>
    </div>
  );

  const renderPane = () => {
    // When collapsed, render a slim tab with an expand button
    if (mode === 'collapsed') {
      return (
        <div className="relative h-full">
          <button
            className="battle-sidebar__edge-toggle absolute top-1/2 -translate-y-1/2 z-10 w-6 h-20 flex items-center justify-center transition-all duration-200 rounded-r shadow-md hover:shadow-lg border-l-2"
            style={{
              right: id === 'left' ? '0' : 'auto',
              left: id === 'right' ? '0' : 'auto',
              transform: `translateY(-50%) ${id === 'right' ? 'rotate-180' : ''}`,
              opacity: 1
            }}
            onClick={() => setMode('icons')}
            aria-label={`Expand ${id} sidebar`}
            title={`Expand ${id} sidebar`}
          >
            <ChevronRight size={16} className="text-current" />
          </button>
        </div>
      );
    }

    const isIcons = mode === 'icons';
    
    return (
      <div className="relative flex h-full min-h-0 w-full">
        {/* Collapse Button (visible when expanded) */}
        {!isIcons && !isCompact && (
          <button
            className={`battle-sidebar__edge-toggle absolute top-1/2 -translate-y-1/2 z-10 w-6 h-20 flex items-center justify-center
              transition-all duration-200 rounded-r shadow-md hover:shadow-lg border-l-2
              ${id === 'left' ? '-right-6' : '-left-6 rotate-180'}
              opacity-0 group-hover:opacity-100`}
            onClick={() => setMode('icons')}
            aria-label={`Collapse ${id} sidebar`}
            title={`Collapse ${id} sidebar`}
          >
            <ChevronRight size={16} className="text-current" />
          </button>
        )}

        {/* Icon Rail - Only show in icons mode */}
        {isIcons && icons && (
          <div className="battle-sidebar__icon-rail flex flex-col items-center gap-2 py-2 border-r h-full">
            <div className="h-full p-2 flex items-center min-h-0">
              <div className="flex flex-col gap-4 items-center">
                {icons}
              </div>
            </div>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
              <ControlButtons />
            </div>
          </div>
        )}

        {/* Expanded Content */}
        {!isIcons && (
          <div className="w-full h-full min-h-0 flex flex-col overflow-hidden">
            {isCompact && (
              <div className="battle-sidebar__drawer-header">
                <span>{drawerTitle}</span>
                <button
                  type="button"
                  className="battle-sidebar__drawer-close"
                  onClick={onRequestClose}
                  aria-label={`Close ${drawerTitle}`}
                  title={`Close ${drawerTitle}`}
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
            )}
            <div className="battle-sidebar-scroll flex-1 min-h-0">
              {children}
            </div>
            {resizable && !isCompact && (
              <div 
                className={`battle-sidebar__resize-edge absolute top-0 bottom-0 w-1 cursor-ew-resize transition-colors ${
                  id === 'left' ? 'right-0' : 'left-0'
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startX = e.clientX;
                  const startWidth = px;
                  
                  const onMouseMove = (e: MouseEvent) => {
                    const dx = e.clientX - startX;
                    const newWidth = id === 'left' 
                      ? Math.max(min, Math.min(max, startWidth + dx))
                      : Math.max(min, Math.min(max, startWidth - dx));
                    setPx(newWidth);
                  };
                  
                  const onMouseUp = () => {
                    document.removeEventListener('mousemove', onMouseMove);
                    document.removeEventListener('mouseup', onMouseUp);
                  };
                  
                  document.addEventListener('mousemove', onMouseMove);
                  document.addEventListener('mouseup', onMouseUp);
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className={`relative flex h-full min-h-0 ${id === 'left' ? 'flex-row' : 'flex-row-reverse'} ${className}`}
      style={mode === 'expanded' ? { width: `${px}px` } : undefined}
      data-sidebar-mode={mode}
      data-sidebar-id={id}
    >
      {/* Collapse/Expand Toggle Button (always visible when not expanded) */}
      {mode !== 'expanded' && !isCompact && (
        <button
          className="battle-sidebar__edge-toggle absolute top-1/2 -translate-y-1/2 z-10 w-6 h-20 flex items-center justify-center transition-all duration-200 rounded-r shadow-md hover:shadow-lg border-l-2"
          style={{
            right: id === 'left' ? '0' : 'auto',
            left: id === 'right' ? '0' : 'auto',
            transform: `translateY(-50%) ${id === 'right' ? 'rotate-180' : ''}`,
            opacity: 1
          }}
          onClick={() => setMode('expanded')}
          aria-label={`Expand ${id} sidebar`}
          title={`Expand ${id} sidebar`}
        >
          <ChevronRight size={16} className="text-current" />
        </button>
      )}

      {/* Main Pane */}
      <div className="h-full min-h-0 w-full group">
        {id === 'left' && renderPane()}
        <div className="min-w-0 flex-1">
          {center}
          {/* Bottom controls - Horizontal controls only */}
          {mode === 'expanded' && !isCompact && (
            <div className="absolute bottom-4 left-4 z-10">
              <ControlButtons />
            </div>
          )}
        </div>
        {id === 'right' && renderPane()}
      </div>
    </div>
  );
}
